import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsUpDown, Eye, EyeOff, Info, Loader2,
    RefreshCw, Search,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { getRunSegments, getRuns, resolveSlugs } from '@/services/api/price-structure';
import type { OptimizerGuard, Run, SegmentResult, Setup } from '@/types/price-structure';

import { RuleChart } from './RuleChart';

import {
    ENGINES, GUARD_LABEL, OUTCOME_LABEL, OUTCOME_STYLE, THERMOMETERS, asArray, engineOf,
    formatUsd, linearParamsOf, linearSummary,
} from '../constants';

type Level = 'post' | 'país' | 'bloco';

const levelOf = (post: string, country: string): Level =>
    post !== ANY ? 'post' : country !== ANY ? 'país' : 'bloco';

/**
 * As regras do domínio no formato da Active View: uma linha por combinação
 * URI × bloco × país × device, com o desempenho no GAM e a cascata que o motor
 * gerou para ela.
 *
 * A fonte é a auditoria da última execução de verdade (agendada ou manual —
 * simulação não tem setup_id e não aparece aqui). Ela guarda, por combinação,
 * as métricas que o motor leu e os steps que decidiu.
 */

type SortKey =
    | 'revenue' | 'impressions' | 'ecpm' | 'pmr' | 'top' | 'uri' | 'block' | 'country'
    | 'rule' | 'match' | 'opportunities';

interface RuleRow {
    id: number;
    level: Level;
    adUnit: string;     // chave do bloco no audit: rewarded_mobile
    post: string;
    block: string;
    device: string;
    country: string;
    impressions: number;
    ecpm: number;
    pmr: number;
    revenue: number;
    steps: number[];
    outcome: SegmentResult['outcome'];
    reason: string | null;
    // Otimizador (v4)
    rule: number | null;
    rulePrev: number | null;
    matchRate: number | null;
    opportunities: number | null;
    factor: number | null;
    guard: OptimizerGuard | null;
}

/** `*` no audit = a entrada vale para todos os posts ou todos os países. */
const ANY = '*';

const parseSteps = (steps: SegmentResult['steps']): number[] => {
    if (Array.isArray(steps)) return steps;
    if (typeof steps === 'string' && steps) {
        try {
            return JSON.parse(steps);
        } catch {
            return [];
        }
    }
    return [];
};

/** Como a dash chama o dispositivo do nome do ad unit (DESKTOP = Web). */
const DEVICE_LABEL: Record<string, string> = { mobile: 'Mobile', desktop: 'Web', web: 'Web' };

/** `rewarded_mobile` → bloco `rewarded`, device `Mobile`. Fora do padrão, como veio no nome. */
const splitBlock = (key: string) => {
    const at = key.lastIndexOf('_');
    if (at <= 0) return { block: key, device: '—' };
    const device = key.slice(at + 1);
    return { block: key.slice(0, at), device: DEVICE_LABEL[device] ?? device.toUpperCase() };
};

const money = (v: number) =>
    `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPrice = (v: number) => (v < 1 ? v.toFixed(2) : String(v));

export function RulesView({ setup, domainId, domainName }: {
    setup: Setup;
    domainId: number;
    domainName?: string;
}) {
    const engine = engineOf(setup);
    const [slugs, setSlugs] = useState<Record<string, string | null>>({});
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        block: '', country: '', device: '', outcome: '', level: '',
    });
    const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'revenue', desc: true });
    const [open, setOpen] = useState<Set<number>>(new Set());
    const [showGam, setShowGam] = useState(true);
    const [showOpt, setShowOpt] = useState(true);

    const runsQuery = useQuery({
        queryKey: ['price-structure-runs', setup.id],
        queryFn: () => getRuns(setup.id),
    });
    const lastRun = asArray<Run>(runsQuery.data).find((r) => r.status === 'completed') ?? null;

    const segmentsQuery = useQuery({
        queryKey: ['price-structure-run-segments', lastRun?.id],
        queryFn: () => getRunSegments(lastRun!.id),
        enabled: !!lastRun,
    });

    const rows = useMemo<RuleRow[]>(
        () =>
            asArray<SegmentResult>(segmentsQuery.data)
                // Desde 29/09 o v4 só tem a combinação completa; as linhas `*`
                // ("Todos os posts", "Todos os países") são de runs anteriores.
                .filter((s) => s.custom_value !== ANY && s.country !== ANY)
                .map((s) => {
                    const { block, device: fromName } = splitBlock(s.ad_unit);
                    const device = s.device_label ?? fromName;
                    const impressions = s.impressions ?? 0;
                    const ecpm = Number(s.ecpm ?? 0);
                    return {
                        id: s.id,
                        level: levelOf(s.custom_value, s.country),
                        adUnit: s.ad_unit,
                        post: s.custom_value,
                        block,
                        device,
                        country: s.country,
                        impressions,
                        ecpm,
                        pmr: Number(s.pmr ?? 0),
                        // O audit guarda eCPM e impressões; a receita sai deles.
                        revenue: (ecpm * impressions) / 1000,
                        steps: parseSteps(s.steps),
                        outcome: s.outcome,
                        reason: s.degradation_reason,
                        rule: s.rule == null ? null : Number(s.rule),
                        rulePrev: s.rule_prev == null ? null : Number(s.rule_prev),
                        matchRate: s.match_rate == null ? null : Number(s.match_rate),
                        opportunities: s.opportunities ?? null,
                        factor: s.factor == null ? null : Number(s.factor),
                        guard: s.guard ?? null,
                    };
                }),
        [segmentsQuery.data],
    );

    // A URI é o que se reconhece de relance; o id do post vem do GAM.
    const postIds = useMemo(
        () => [...new Set(rows.map((r) => r.post).filter((p) => p !== ANY))].slice(0, 500),
        [rows],
    );
    useEffect(() => {
        if (!postIds.length) return;
        let cancelled = false;
        resolveSlugs(domainId, postIds)
            .then((data) => !cancelled && setSlugs(data.slugs))
            .catch(() => undefined); // sem WordPress, a coluna fica com o id
        return () => {
            cancelled = true;
        };
    }, [domainId, postIds]);

    const uriOf = (post: string) =>
        post === ANY ? '(todos os posts)' : slugs[post] ? `/${slugs[post]}` : `post ${post}`;
    const countryOf = (country: string) => (country === ANY ? '(todos)' : country);

    const options = useMemo(() => {
        const uniq = (values: string[]) => [...new Set(values)].sort();
        return {
            block: uniq(rows.map((r) => r.block)),
            country: uniq(rows.map((r) => r.country)),
            device: uniq(rows.map((r) => r.device)),
            outcome: uniq(rows.map((r) => r.outcome)),
            level: uniq(rows.map((r) => r.level)),
        };
    }, [rows]);

    const needle = search.trim().toLowerCase();
    const visible = useMemo(() => {
        const filtered = rows.filter(
            (r) =>
                (!filters.block || r.block === filters.block) &&
                (!filters.country || r.country === filters.country) &&
                (!filters.device || r.device === filters.device) &&
                (!filters.outcome || r.outcome === filters.outcome) &&
                (!filters.level || r.level === filters.level) &&
                (!needle ||
                    r.post.includes(needle) ||
                    (slugs[r.post] ?? '').toLowerCase().includes(needle)),
        );
        const value = (r: RuleRow): number | string => {
            switch (sort.key) {
                case 'uri': return uriOf(r.post);
                case 'block': return r.block;
                case 'country': return r.country;
                case 'top': return r.steps[0] ?? -1;
                case 'rule': return r.rule ?? -1;
                case 'match': return r.matchRate ?? -1;
                case 'opportunities': return r.opportunities ?? -1;
                default: return r[sort.key];
            }
        };
        return filtered.sort((a, b) => {
            const va = value(a);
            const vb = value(b);
            const cmp = typeof va === 'number' ? va - (vb as number) : String(va).localeCompare(String(vb));
            return sort.desc ? -cmp : cmp;
        });
        // uriOf depende de slugs, que já está na lista
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, filters, needle, sort, slugs]);

    const totals = useMemo(() => {
        const withRule = rows.filter((r) => r.steps.length > 0);
        // Só combinações completas: cada impressão está numa linha só.
        const impressions = rows.reduce((acc, r) => acc + r.impressions, 0);
        const revenue = rows.reduce((acc, r) => acc + r.revenue, 0);
        return {
            rules: withRule.length,
            without: rows.length - withRule.length,
            revenue,
            impressions,
            ecpm: impressions ? (revenue / impressions) * 1000 : 0,
        };
    }, [rows]);

    const toggleSort = (key: SortKey) =>
        setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: true }));

    const toggleOpen = (id: number) =>
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const refresh = () => {
        runsQuery.refetch();
        segmentsQuery.refetch();
    };

    const loading = runsQuery.isLoading || segmentsQuery.isLoading;
    const engineInfo = ENGINES.find((e) => e.value === engine);
    const tuning = linearParamsOf(setup);
    // As colunas do otimizador só aparecem quando o run foi do v4 com ele.
    const optimized = rows.some((r) => r.guard !== null);
    const thermometerLabel =
        THERMOMETERS.find((t) => t.value === tuning.thermometer)?.label ?? tuning.thermometer;
    const gam = showGam;
    const opt = optimized && showOpt;
    const columns = 5 + (gam ? 4 : 0) + 2 + (opt ? 4 : 0);

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-blue-100 bg-gradient-to-b from-blue-50/60 to-white">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-blue-100 px-5 py-4">
                    <div>
                        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                            Modelo de preço
                        </div>
                        <div className="mt-0.5 text-lg font-semibold text-gray-900">
                            {engineInfo?.label ?? engine}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                            {engine === 'v4'
                                ? linearSummary(linearParamsOf(setup), setup.steps_count)
                                : engineInfo?.detail}
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={refresh}>
                        <RefreshCw className="mr-2 h-3.5 w-3.5" /> Atualizar
                    </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 px-5 py-4 text-sm sm:grid-cols-3 lg:grid-cols-6">
                    <Stat label="Regras" value={String(totals.rules)} />
                    <Stat
                        label="Sem regra própria"
                        value={String(totals.without)}
                        hint="Caem na cascata do país ou do bloco"
                    />
                    <Stat label="Receita" value={money(totals.revenue)} />
                    <Stat label="Impressões" value={totals.impressions.toLocaleString('pt-BR')} />
                    <Stat label="eCPM" value={money(totals.ecpm)} />
                    <Stat
                        label={engine === 'v4' ? 'Mín. receita' : 'Mín. impressões'}
                        value={
                            engine === 'v4'
                                ? formatUsd(linearParamsOf(setup).min_revenue)
                                : setup.min_impressions.toLocaleString('pt-BR')
                        }
                    />
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {lastRun && (
                    <span
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600"
                        title={`Dados do GAM de ${lastRun.window_start} a ${lastRun.window_end} · execução #${lastRun.id}`}
                    >
                        <Info className="h-3.5 w-3.5 text-gray-400" />
                        Atualizado às{' '}
                        {new Date(lastRun.completed_at ?? lastRun.started_at).toLocaleString('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                        })}
                    </span>
                )}
                <div className="relative min-w-[220px] flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar URI ou id do post..."
                        className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                {options.level.length > 1 && (
                    <FilterSelect label="Nível" value={filters.level} options={options.level}
                        onChange={(level) => setFilters((f) => ({ ...f, level }))} />
                )}
                <FilterSelect label="Bloco" value={filters.block} options={options.block}
                    onChange={(block) => setFilters((f) => ({ ...f, block }))} />
                <FilterSelect label="País" value={filters.country} options={options.country}
                    render={countryOf}
                    onChange={(country) => setFilters((f) => ({ ...f, country }))} />
                <FilterSelect label="Device" value={filters.device} options={options.device}
                    onChange={(device) => setFilters((f) => ({ ...f, device }))} />
                <FilterSelect
                    label="Situação"
                    value={filters.outcome}
                    options={options.outcome}
                    render={(o) => OUTCOME_LABEL[o as SegmentResult['outcome']] ?? o}
                    onChange={(outcome) => setFilters((f) => ({ ...f, outcome }))}
                />
                <span className="ml-auto text-xs text-gray-500">{visible.length} linhas</span>
                {/* Esconder um grupo inteiro, como na Active View. */}
                <GroupToggle
                    label="Desempenho"
                    on={showGam}
                    onClick={() => setShowGam((v) => !v)}
                    tone="text-amber-700"
                />
                {optimized && (
                    <GroupToggle
                        label="Otimização"
                        on={showOpt}
                        onClick={() => setShowOpt((v) => !v)}
                        tone="text-emerald-700"
                    />
                )}
            </div>

            {loading && (
                <div className="flex items-center justify-center gap-3 py-12 text-gray-600">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> Carregando...
                </div>
            )}

            {!loading && !lastRun && (
                <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white py-12 text-center text-sm text-gray-500">
                    Nenhuma execução ainda. Use “Rodar agora” no card do domínio.
                </div>
            )}

            {!loading && lastRun && (
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-gray-600">
                            <tr className="text-[11px] uppercase tracking-wide">
                                <th colSpan={5} className="bg-violet-50 px-3 py-2 text-center font-medium text-violet-800">
                                    ● Identificação
                                </th>
                                {gam && (
                                    <th colSpan={4} className="bg-amber-50 px-3 py-2 text-center font-medium text-amber-800">
                                        ● Desempenho no GAM
                                    </th>
                                )}
                                <th colSpan={2} className="bg-sky-50 px-3 py-2 text-center font-medium text-sky-800">
                                    ● Cascata ({engine})
                                </th>
                                {opt && (
                                    <th colSpan={4} className="bg-emerald-50 px-3 py-2 text-center font-medium text-emerald-800">
                                        ● Otimização · {thermometerLabel}, alvo {tuning.target}%
                                    </th>
                                )}
                            </tr>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <SortTh label="URI" k="uri" sort={sort} onSort={toggleSort} />
                                <SortTh label="Bloco" k="block" sort={sort} onSort={toggleSort} />
                                <SortTh label="País" k="country" sort={sort} onSort={toggleSort} />
                                <th className="px-3 py-2.5 font-medium">Device</th>
                                <th className="px-3 py-2.5 font-medium">Situação</th>
                                {gam && (
                                    <>
                                        <SortTh label="Receita" k="revenue" sort={sort} onSort={toggleSort} right />
                                        <SortTh label="Impressões" k="impressions" sort={sort} onSort={toggleSort} right />
                                        <SortTh label="eCPM" k="ecpm" sort={sort} onSort={toggleSort} right />
                                        <SortTh label="PMR" k="pmr" sort={sort} onSort={toggleSort} right />
                                    </>
                                )}
                                <SortTh label="Abre em" k="top" sort={sort} onSort={toggleSort} right />
                                <th className="px-3 py-2.5 font-medium">Steps</th>
                                {opt && (
                                    <>
                                        <SortTh label="Regra" k="rule" sort={sort} onSort={toggleSort} right />
                                        <SortTh label="Preench. ontem" k="match" sort={sort} onSort={toggleSort} right />
                                        <SortTh label="Oportunidades" k="opportunities" sort={sort} onSort={toggleSort} right />
                                        <th className="px-3 py-2.5 font-medium">Ajuste</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {visible.map((r) => {
                                const expanded = open.has(r.id);
                                return (
                                    <Fragment key={r.id}>
                                        <tr className="hover:bg-gray-50/60">
                                            <td className="max-w-[280px] px-3 py-2.5">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleOpen(r.id)}
                                                    className="flex w-full items-center gap-1.5 text-left"
                                                    title={slugs[r.post] ? `post ${r.post}` : undefined}
                                                >
                                                    {expanded ? (
                                                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                                    ) : (
                                                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                                    )}
                                                    <span className="truncate text-gray-800">{uriOf(r.post)}</span>
                                                </button>
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-700">{r.block}</td>
                                            <td className="px-3 py-2.5 text-gray-700">{countryOf(r.country)}</td>
                                            <td className="px-3 py-2.5 text-gray-700">{r.device}</td>
                                            <td className="px-3 py-2.5">
                                                <span
                                                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${OUTCOME_STYLE[r.outcome]}`}
                                                    title={r.reason ?? undefined}
                                                >
                                                    {OUTCOME_LABEL[r.outcome]}
                                                </span>
                                            </td>
                                            {gam && (
                                                <>
                                                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-gray-900">
                                                        {money(r.revenue)}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                                                        {r.impressions.toLocaleString('pt-BR')}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                                                        {money(r.ecpm)}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                                                        {r.pmr.toFixed(1)}%
                                                    </td>
                                                </>
                                            )}
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                                                {r.steps.length ? `$${fmtPrice(r.steps[0])}` : '—'}
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600">
                                                {r.steps.length
                                                    ? `${r.steps.length} · até $${fmtPrice(r.steps[r.steps.length - 1])}`
                                                    : 'usa país/bloco'}
                                            </td>
                                            {opt && (
                                                <>
                                                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-gray-900">
                                                        {r.rule == null ? '—' : money(r.rule)}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                                                        {r.matchRate == null ? '—' : `${r.matchRate.toFixed(1)}%`}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                                                        {r.opportunities == null
                                                            ? '—'
                                                            : r.opportunities.toLocaleString('pt-BR')}
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <AdjustBadge row={r} />
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                        {expanded && (
                                            <tr className="bg-gray-50/60">
                                                <td colSpan={columns} className="px-10 py-3 text-xs text-gray-600">
                                                    <div className="mb-3">
                                                        <RuleChart
                                                            setupId={setup.id}
                                                            adUnit={r.adUnit}
                                                            country={r.country}
                                                            customValue={r.post}
                                                            title={uriOf(r.post)}
                                                            onClose={() => toggleOpen(r.id)}
                                                        />
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        {r.steps.length ? (
                                                            r.steps.map((price, i) => (
                                                                <span
                                                                    key={i}
                                                                    className="rounded-md border border-sky-200 bg-white px-2 py-0.5 font-mono text-sky-900"
                                                                >
                                                                    {i + 1}º ${fmtPrice(price)}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span>
                                                                Sem cascata própria — esta combinação usa a do país
                                                                ou a do bloco.
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-gray-500">
                                                        <span>post {r.post}</span>
                                                        {slugs[r.post] && domainName && (
                                                            <a
                                                                href={`https://${domainName}/${slugs[r.post]}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-blue-600 hover:underline"
                                                            >
                                                                abrir a página
                                                            </a>
                                                        )}
                                                        {r.reason && <span className="text-amber-700">{r.reason}</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/** O que o otimizador fez com a regra hoje, e por quê. */
function AdjustBadge({ row }: { row: RuleRow }) {
    if (!row.guard) return <span className="text-xs text-gray-400">—</span>;

    const pct = row.factor != null ? (row.factor - 1) * 100 : 0;
    const moved = Math.abs(pct) >= 0.05;
    const tone =
        row.guard === 'ajustado'
            ? pct >= 0
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700'
            : row.guard === 'piso' || row.guard === 'teto'
              ? 'bg-amber-100 text-amber-800'
              : 'bg-gray-100 text-gray-600';
    const detail =
        row.rulePrev != null && row.rule != null
            ? `regra ${money(row.rulePrev)} → ${money(row.rule)}`
            : undefined;

    return (
        <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tone}`} title={detail}>
            {moved && row.guard !== 'semente'
                ? `${pct > 0 ? '↑' : '↓'} ${Math.abs(pct).toFixed(1)}%${row.guard === 'ajustado' ? '' : ` · ${GUARD_LABEL[row.guard]}`}`
                : GUARD_LABEL[row.guard]}
        </span>
    );
}

/** Mostra ou esconde um grupo de colunas inteiro. */
function GroupToggle({ label, on, onClick, tone }: {
    label: string;
    on: boolean;
    onClick: () => void;
    tone: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={`${on ? 'Esconder' : 'Mostrar'} as colunas de ${label.toLowerCase()}`}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                on ? `border-gray-200 bg-white ${tone}` : 'border-dashed border-gray-200 text-gray-400'
            }`}
        >
            {on ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            {label}
        </button>
    );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div title={hint}>
            <div className="text-xs font-medium text-gray-500">{label}</div>
            <div className="mt-0.5 text-base text-gray-900">{value}</div>
        </div>
    );
}

function SortTh({ label, k, sort, onSort, right }: {
    label: string;
    k: SortKey;
    sort: { key: SortKey; desc: boolean };
    onSort: (key: SortKey) => void;
    right?: boolean;
}) {
    const active = sort.key === k;
    return (
        <th className={`px-3 py-2.5 font-medium ${right ? 'text-right' : ''}`}>
            <button
                type="button"
                onClick={() => onSort(k)}
                className={`inline-flex items-center gap-1 ${active ? 'text-gray-900' : 'hover:text-gray-900'}`}
            >
                {label}
                {active ? (
                    sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                ) : (
                    <ChevronsUpDown className="h-3 w-3 text-gray-300" />
                )}
            </button>
        </th>
    );
}

function FilterSelect({ label, value, options, onChange, render }: {
    label: string;
    value: string;
    options: string[];
    onChange: (value: string) => void;
    render?: (value: string) => string;
}) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                value ? 'border-blue-300 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-600'
            }`}
        >
            <option value="">{label}</option>
            {options.map((o) => (
                <option key={o} value={o}>
                    {render ? render(o) : o}
                </option>
            ))}
        </select>
    );
}
